import { useEffect, useMemo, useState } from 'react';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';
import { getLocalities } from '@/services/clients.js';

const EMPTY_DOMICILIO = {
  id: null,
  alias: '',
  tipo: 'DESTINO',
  direccion: '',
  cp: '',
  provinciaId: '',
  localidadId: '',
  contacto: '',
  horario: '',
  observacionesAcceso: '',
  lat: '',
  lng: '',
  esDefault: false,
  activo: true,
};

const TIPOS = ['ORIGEN', 'DESTINO', 'FACTURACION', 'RETIRO', 'DEVOLUCION'];

export default function DomiciliosTab({ data = [], onChange, provinces = [] }) {
  const [domicilios, setDomicilios] = useState(data || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState(EMPTY_DOMICILIO);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [localitiesMap, setLocalitiesMap] = useState({});
  const [loadingLocalities, setLoadingLocalities] = useState(false);

  useEffect(() => {
    setDomicilios(data || []);
  }, [data]);

  const visibleDomicilios = useMemo(
    () => domicilios.filter(domicilio => domicilio.activo !== false),
    [domicilios]
  );

  const resetForm = () => {
    setFormState(EMPTY_DOMICILIO);
    setEditingIndex(null);
    setErrors({});
  };

  const openNew = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (index) => {
    setFormState({ ...domicilios[index] });
    setEditingIndex(index);
    setErrors({});
    setModalOpen(true);
  };

  const resolveProvinciaLabel = (id) => provinces.find(p => String(p.id) === String(id))?.nombre || '-';
  const resolveLocalidadLabel = (provinciaId, localidadId) =>
    localitiesMap[provinciaId]?.find(l => String(l.id) === String(localidadId))?.nombre || '-';

  const ensureLocalities = async (provinciaId) => {
    if (!provinciaId || localitiesMap[provinciaId]) return;
    setLoadingLocalities(true);
    try {
      const locs = await getLocalities(provinciaId);
      setLocalitiesMap(prev => ({ ...prev, [provinciaId]: locs || [] }));
    } finally {
      setLoadingLocalities(false);
    }
  };

  useEffect(() => {
    if (formState.provinciaId) {
      ensureLocalities(formState.provinciaId);
    }
  }, [formState.provinciaId]);

  useEffect(() => {
    visibleDomicilios.forEach(domicilio => {
      if (domicilio.provinciaId) {
        ensureLocalities(domicilio.provinciaId);
      }
    });
  }, [visibleDomicilios]);

  const validateForm = () => {
    const nextErrors = {};
    if (!formState.direccion.trim()) {
      nextErrors.direccion = 'La dirección es obligatoria.';
    }
    if (!formState.tipo) {
      nextErrors.tipo = 'Seleccioná un tipo.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    let next = [...domicilios];
    let updated = {
      ...formState,
      alias: formState.alias?.trim() || '',
      direccion: formState.direccion?.trim() || '',
      cp: formState.cp?.trim() || '',
      contacto: formState.contacto?.trim() || '',
      horario: formState.horario?.trim() || '',
      observacionesAcceso: formState.observacionesAcceso?.trim() || '',
      lat: formState.lat?.trim() || '',
      lng: formState.lng?.trim() || '',
      activo: formState.activo !== false,
    };

    if (updated.esDefault) {
      next = next.map(item => ({ ...item, esDefault: false }));
    }

    if (editingIndex != null) {
      next[editingIndex] = { ...next[editingIndex], ...updated };
    } else {
      next = [...next, updated];
    }

    setDomicilios(next);
    onChange?.(next);
    setModalOpen(false);
    resetForm();
  };

  const handleDelete = (index) => {
    const next = domicilios.map((item, idx) =>
      idx === index ? { ...item, activo: false } : item
    );
    setDomicilios(next);
    onChange?.(next);
  };

  const handleChange = (field, value) => {
    if (field === 'provinciaId') {
      setFormState(prev => ({ ...prev, provinciaId: value, localidadId: '' }));
      return;
    }
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[rgb(var(--fg))]">Domicilios operativos</h3>
          <p className="text-sm text-[rgba(var(--muted),0.85)]">
            Administrá múltiples domicilios de origen, destino y facturación.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))]"
          onClick={openNew}
        >
          Agregar domicilio
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))]">
        <table className="min-w-full text-sm">
          <thead className="border-b border-[rgba(var(--border),0.4)] text-left text-[rgba(var(--muted),0.9)]">
            <tr>
              <th className="px-4 py-3">Alias</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Dirección</th>
              <th className="px-4 py-3">CP</th>
              <th className="px-4 py-3">Provincia</th>
              <th className="px-4 py-3">Localidad</th>
              <th className="px-4 py-3">Contacto</th>
              <th className="px-4 py-3">Horario</th>
              <th className="px-4 py-3">Default</th>
              <th className="px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleDomicilios.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-[rgba(var(--muted),0.8)]" colSpan={10}>
                  No hay domicilios cargados.
                </td>
              </tr>
            ) : (
              visibleDomicilios.map((domicilio, index) => (
                <tr key={`${domicilio.direccion}-${index}`} className="border-b border-[rgba(var(--border),0.2)]">
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{domicilio.alias || '-'}</td>
                  <td className="px-4 py-3 font-medium text-[rgb(var(--fg))]">{domicilio.tipo}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{domicilio.direccion}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{domicilio.cp || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{resolveProvinciaLabel(domicilio.provinciaId)}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">
                    {resolveLocalidadLabel(domicilio.provinciaId, domicilio.localidadId)}
                  </td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{domicilio.contacto || '-'}</td>
                  <td className="px-4 py-3 text-[rgba(var(--muted),0.9)]">{domicilio.horario || '-'}</td>
                  <td className="px-4 py-3">
                    {domicilio.esDefault ? (
                      <span className="rounded-full bg-[rgba(var(--brand),0.15)] px-2 py-0.5 text-xs text-[rgb(var(--brand))]">
                        Sí
                      </span>
                    ) : (
                      <span className="text-xs text-[rgba(var(--muted),0.7)]">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs text-[rgb(var(--brand))] hover:underline"
                        onClick={() => openEdit(index)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-xs text-[rgb(var(--muted))] hover:text-[rgb(var(--brand))]"
                        onClick={() => handleDelete(index)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ModalBase
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        labelledBy="modal-domicilios-titulo"
        className="modal modal-content w-full max-w-3xl p-5 md:p-6 focus-visible:outline-none"
      >
        <div className="space-y-4">
          <div className="border-b border-[rgba(var(--border),0.6)] pb-3">
            <h4 id="modal-domicilios-titulo" className="text-lg font-semibold text-[rgb(var(--fg))]">
              {editingIndex != null ? 'Editar domicilio' : 'Nuevo domicilio'}
            </h4>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Alias</label>
              <input
                value={formState.alias}
                onChange={e => handleChange('alias', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Tipo *</label>
              <select
                value={formState.tipo}
                onChange={e => handleChange('tipo', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              >
                {TIPOS.map(tipo => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
              {errors.tipo ? <p className="text-xs text-[rgb(var(--brand))]">{errors.tipo}</p> : null}
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Dirección *</label>
              <input
                value={formState.direccion}
                onChange={e => handleChange('direccion', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
              {errors.direccion ? <p className="text-xs text-[rgb(var(--brand))]">{errors.direccion}</p> : null}
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">CP</label>
              <input
                value={formState.cp}
                onChange={e => handleChange('cp', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Provincia</label>
              <select
                value={formState.provinciaId}
                onChange={e => handleChange('provinciaId', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              >
                <option value="">Seleccionar...</option>
                {provinces.map(provincia => (
                  <option key={provincia.id} value={provincia.id}>
                    {provincia.nombre || provincia.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Localidad</label>
              <select
                value={formState.localidadId}
                onChange={e => handleChange('localidadId', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
                disabled={!formState.provinciaId || loadingLocalities}
              >
                <option value="">{loadingLocalities ? 'Cargando...' : 'Seleccionar...'}</option>
                {(localitiesMap[formState.provinciaId] || []).map(localidad => (
                  <option key={localidad.id} value={localidad.id}>
                    {localidad.nombre || localidad.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Contacto</label>
              <input
                value={formState.contacto}
                onChange={e => handleChange('contacto', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Horario</label>
              <input
                value={formState.horario}
                onChange={e => handleChange('horario', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Lat</label>
              <input
                value={formState.lat}
                onChange={e => handleChange('lat', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Lng</label>
              <input
                value={formState.lng}
                onChange={e => handleChange('lng', e.target.value)}
                className="h-10 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 text-sm"
              />
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-sm text-[rgba(var(--muted),0.9)]">Observaciones acceso</label>
              <textarea
                value={formState.observacionesAcceso}
                onChange={e => handleChange('observacionesAcceso', e.target.value)}
                className="min-h-[90px] w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--subpanel))] px-3 py-2 text-sm"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
            <input
              type="checkbox"
              checked={formState.esDefault}
              onChange={e => handleChange('esDefault', e.target.checked)}
              className="h-4 w-4 rounded border-[rgba(var(--border),0.6)]"
            />
            Marcar como domicilio por defecto
          </label>

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              className="border border-[rgba(var(--border),0.6)] bg-transparent"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="border border-[rgb(var(--brand))] bg-[rgb(var(--brand))] text-[rgb(var(--fg))]"
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      </ModalBase>
    </div>
  );
}
